/**
 * Shared utilities for results controllers (brand shop, landing pages).
 *
 * Extracted to avoid duplication between useBrandResultsController and
 * useLandingResultsController. Both controllers share the same filter,
 * facet, and product-list manipulation logic.
 */

import { PRICE_RANGE_MAX_DEFAULT, PRICE_RANGE_MIN_DEFAULT } from "@/hooks/useResultsFilters";
import { HIDDEN_ADVANCED_ATTRIBUTE_KEYS } from "@/lib/filter-visibility";
import { copyFacet } from "@/lib/facet-provenance";
import type { Product } from "@/lib/product-utils";
import { normalizePriceBounds } from "@/lib/results-page-utils";
import type { ShopperFilters } from "@/lib/shopper-search";

import type { Facet as CatalogFacet } from "openapi/shop_agent/client/types.gen";

export type SearchParamFacet = {
  name: string;
  values: string[];
  source?: CatalogFacet["source"];
  stage?: CatalogFacet["stage"];
};

export const CATEGORY_NAVIGATION_SYSTEM_FACETS = new Set([
  "age_group",
  "age_groups",
  "gender",
  "is_activewear",
  "target_gender",
]);

export function getSearchParamFacets(
  filters: ShopperFilters | null | undefined
): SearchParamFacet[] {
  // Keep source/stage: the relaxation cascade reads them to tell a filter
  // the shopper set from one the generator inferred.
  return (filters?.facets ?? []).map((facet: CatalogFacet) => copyFacet(facet));
}

/**
 * Insert or replace a facet by name. The result is the shopper's choice unless
 * `provenance` says otherwise (pass the original facet's `source`/`stage` when
 * carrying a facet over unchanged).
 */
export function upsertFacet(
  facets: SearchParamFacet[],
  facetName: string,
  values: string[],
  provenance: Pick<SearchParamFacet, "source" | "stage"> = { source: "caller", stage: null }
): SearchParamFacet[] {
  const nextFacets = [...facets];
  const existingIdx = nextFacets.findIndex((facet) => facet.name === facetName);
  const facet: SearchParamFacet = { name: facetName, values: [...values], ...provenance };

  if (existingIdx >= 0) {
    nextFacets[existingIdx] = facet;
  } else {
    nextFacets.push(facet);
  }

  return nextFacets;
}

export function deriveInitialFilters(filters: ShopperFilters) {
  const facets = getSearchParamFacets(filters);
  const colorFacet = facets.find((facet) => facet.name === "color_family");
  const { min: normalizedMin, max: normalizedMax } = normalizePriceBounds(
    filters.price_min,
    filters.price_max
  );

  const advanced: Record<string, string[]> = {};
  facets.forEach((facet) => {
    if (
      facet.name.startsWith("category_path.depth_") ||
      facet.name === "brand_name" ||
      HIDDEN_ADVANCED_ATTRIBUTE_KEYS.has(facet.name)
    ) {
      return;
    }

    if (facet.values.length > 0) {
      advanced[facet.name] = facet.values.map((value) => String(value));
    }
  });

  return {
    priceRange: [
      typeof normalizedMin === "number" ? normalizedMin : PRICE_RANGE_MIN_DEFAULT,
      typeof normalizedMax === "number" ? normalizedMax : PRICE_RANGE_MAX_DEFAULT,
    ] as [number, number],
    colors: colorFacet?.values?.map((value) => String(value)) || [],
    advanced,
  };
}

export function appendUniqueProducts(
  previousResults: Product[],
  newProducts: Product[]
): Product[] {
  const existingProductIds = new Set(
    previousResults.map((product) => String(product.uuid ?? product.id ?? ""))
  );

  return [
    ...previousResults,
    ...newProducts.filter((product) => {
      const id = String(product.uuid ?? product.id ?? "");
      return id.length > 0 && !existingProductIds.has(id);
    }),
  ];
}

export function shouldRetainFacetForCategoryNavigation(facetName: string): boolean {
  return !(
    facetName === "brand_name" ||
    facetName.startsWith("category_path.depth_") ||
    CATEGORY_NAVIGATION_SYSTEM_FACETS.has(facetName)
  );
}
